import { useEffect, useRef, useState } from "react";

// Defers fetching a video's source until it's near the viewport. `autoPlay`
// alone makes browsers start buffering immediately regardless of scroll
// position, and `preload="none"` is unreliable once combined with
// `autoPlay` — so instead we don't mount the real <video>/<source> at all
// until an IntersectionObserver confirms it's about to be seen.
const LazyVideo = ({ src, type = "video/mp4", rootMargin = "200px", ...rest }) => {
  const ref = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (shouldLoad) return;
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldLoad, rootMargin]);

  if (shouldLoad) {
    return (
      <video ref={ref} autoPlay muted loop playsInline {...rest}>
        <source src={src} type={type} />
        Your browser does not support the video tag.
      </video>
    );
  }

  // Placeholder keeps the same style/className/data-aos props so layout and
  // AOS scroll-in animation behave the same either way — swapped for the
  // real <video> the instant it's observed near the viewport.
  return <div ref={ref} {...rest} />;
};

export default LazyVideo;
